'use client';

import {
  FC,
  useState,
  useMemo,
  useContext,
  useEffect,
  useCallback,
} from 'react';
import { EntitySelectorComboboxProps } from './EntitySelectorCombobox.types';
import { Combobox, ResultListItem } from '@/_components';
import { UserContext } from '@/_contexts/UserProvider';
import { getAdminEntities } from '@/_api/v2/admin-entities';
import { getCompanyById } from '@/_api/admin-companies';
import { useDebounce } from '@/_hooks/useDebounce';
import { ToastContext } from '@/_contexts/ToastProvider';
import type { Company } from '@/_types/models/company';
import { DEFAULT_V2_ENTITY_FILTER } from '@/_constants/entities';

const COMPANY_SEARCH_DEBOUNCE = 300 as const;

export const EntitySelectorCombobox: FC<EntitySelectorComboboxProps> = ({
  className,
  preselectedEntityId,
  onChange,
  label,
  isLabelHidden,
  isDisabled = false,
}) => {
  const { token, isAdvisor } = useContext(UserContext);
  const { addToast } = useContext(ToastContext);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>('');
  const [companies, setCompanies] = useState<Array<Company>>([]);
  const [selectedCompany, setSelectedCompany] = useState<Company | undefined>();

  const debouncedSearchValue = useDebounce(
    searchValue,
    COMPANY_SEARCH_DEBOUNCE,
  );

  const comboboxItems: Array<ResultListItem> = useMemo(
    () =>
      companies.map((company) => ({
        name: company.name,
        value: company.id.toString(),
      })),
    [companies],
  );

  const handleSelect = (item: ResultListItem) => {
    if (!item.value) return;

    const companyId: number = parseInt(item.value as string);
    const newSelectedCompany: Company | undefined = companies?.find(
      (c) => c.id === companyId,
    );

    setSelectedCompany(newSelectedCompany);
    onChange?.(companyId);
  };

  const handleClear = () => {
    setSearchValue('');
    setCompanies([]);
    setSelectedCompany(undefined);
    onChange?.(undefined);
  };

  const fetchCompanies = useCallback(
    async (search: string = '') => {
      if (!search) return setCompanies([]);

      try {
        const response = await getAdminEntities({
          ...DEFAULT_V2_ENTITY_FILTER,
          token: token!,
          rows_per_page: 10,
          page: 0,
          search_term: search,
        });
        setCompanies(response);
      } catch (error) {
        addToast('error', 'Failed to fetch companies', { error });
      }
    },
    [addToast, token],
  );

  const getCompany = useCallback(
    async (companyId: number) => {
      if (!token || typeof companyId !== 'number') return;

      try {
        setIsLoading(true);
        const company = await getCompanyById({ token, companyId });

        setSelectedCompany(company);
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    },
    [token],
  );

  useEffect(() => {
    if (typeof preselectedEntityId === 'number') {
      getCompany(preselectedEntityId);
    } else {
      setSelectedCompany(undefined);
    }
  }, [preselectedEntityId, getCompany]);

  useEffect(() => {
    if (!token) return;
    if (!isAdvisor) return;

    fetchCompanies(debouncedSearchValue);
  }, [fetchCompanies, debouncedSearchValue, isAdvisor, addToast, token]);

  return (
    <Combobox
      className={className}
      label={label || 'Select Entity'}
      isLabelHidden={isLabelHidden}
      onSelect={handleSelect}
      value={searchValue}
      isLoading={isLoading}
      onClear={handleClear}
      selectedValue={selectedCompany?.name}
      placeholder="Select Entity"
      setValue={setSearchValue}
      items={comboboxItems}
      isDisabled={isDisabled || isLoading}
    />
  );
};
